"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import type { ExtendedSession, ExtendedUser } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Home,
  ClipboardList,
  BarChart3,
  Building2,
  Settings,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  MessageSquare,
  Shield,
  BookOpen,
  Wrench,
  FileText,
  DollarSign,
  Banknote,
  CalendarDays,
  Users,
  Briefcase,
  FolderOpen,
  UserPlus,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { PropCafeLogo } from "@/components/brand";

/**
 * Main app navigation aligned with PRD workflow:
 * Dashboard → Portfolio → Leasing (pipeline) → Operations → Finance → Tools → AI → Settings
 */
export const navigationSections = [
  {
    label: "Dashboard",
    items: [
      { name: "Overview", href: "/overview", icon: LayoutDashboard },
    ],
  },
  {
    label: "Portfolio",
    items: [
      { name: "Properties & Units", href: "/portfolio", icon: Building2 },
      { name: "Documents", href: "/documents", icon: FolderOpen },
    ],
  },
  {
    label: "Leasing",
    items: [
      { name: "Listings", href: "/leasing", icon: Home },
      { name: "Leads", href: "/leads", icon: UserPlus },
      { name: "Applications", href: "/applications", icon: ClipboardList },
      { name: "Screening", href: "/screening", icon: Shield },
      { name: "Leases", href: "/operations/leases", icon: FileText },
    ],
  },
  {
    label: "Operations",
    items: [
      { name: "Overview", href: "/operations", icon: Briefcase },
      { name: "Tenants", href: "/operations/tenants", icon: Users },
      { name: "Maintenance", href: "/operations/maintenance", icon: Wrench },
      { name: "Vendors", href: "/vendors", icon: MessageSquare },
    ],
  },
  {
    label: "Finance",
    items: [
      { name: "Rent Roll", href: "/operations/rent-roll", icon: Banknote },
      { name: "Payments", href: "/payments", icon: DollarSign },
      { name: "Accounting", href: "/accounting", icon: BarChart3 },
    ],
  },
  {
    label: "Tools",
    items: [
      { name: "Tasks & Calendar", href: "/tasks", icon: CalendarDays },
      { name: "Home Guide", href: "/home-guide", icon: BookOpen },
    ],
  },
  {
    label: "AI",
    items: [
      { name: "Chat", href: "/chat", icon: MessageSquare },
    ],
  },
  {
    label: "Settings",
    items: [{ name: "Settings", href: "/settings", icon: Settings }],
  },
];

export const navigationItems = navigationSections.flatMap(
  (section) => section.items,
);

export const adminNavigation = [
  { name: "Admin Panel", href: "/admin", icon: Shield },
];

export function Sidebar({
  collapsed,
  onCollapsedChange,
  mobileMenuOpen,
  setMobileMenuOpen,
}: {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}) {
  const pathname = usePathname();
  const { data: session } = useSession();

  // Check if user is admin - properly typed
  const extendedSession = session as ExtendedSession | null;
  const user = extendedSession?.user as ExtendedUser | undefined;
  const isAdmin = user?.role === "admin" || user?.roles?.includes("admin");

  return (
    <>
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 h-screen border-r bg-background transition-transform lg:translate-x-0",
          collapsed ? "lg:w-20" : "lg:w-64",
          mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
        style={{
          // Use CSS containment to isolate layout changes and reduce forced reflow
          contain: "layout style",
          // Optimize transitions with will-change hint
          willChange: "width, transform",
          // Explicit transition for smoother animation
          transition:
            "width 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="flex h-full flex-col">
          <div
            className={cn(
              "flex h-16 items-center border-b",
              collapsed ? "px-3" : "px-6 lg:px-4",
            )}
          >
            <div
              className={cn(
                "flex w-full items-center",
                collapsed ? "justify-between gap-2" : "justify-between",
              )}
            >
              <Link
                href="/dashboard"
                className={cn(
                  "flex min-w-0 flex-1 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  collapsed ? "flex-initial shrink-0 px-2" : "px-0",
                )}
                aria-label="PropCafe – go to dashboard"
                title={collapsed ? "PropCafe" : undefined}
                onClick={() => setMobileMenuOpen(false)}
              >
                <PropCafeLogo
                  size="sidebar"
                  iconOnly={collapsed}
                  className="shrink-0"
                />
              </Link>

              {/* Desktop collapse toggle */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex"
                onClick={() => onCollapsedChange(!collapsed)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {collapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </Button>

              {/* Mobile Close Button */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <nav
            className={cn(
              "flex-1 overflow-y-auto py-4",
              collapsed ? "space-y-3 px-2" : "space-y-5 px-3",
            )}
            aria-label="Main navigation"
          >
            {navigationSections.map((section) => (
              <div key={section.label} className="space-y-1">
                <div
                  className={cn(
                    "px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/90",
                    collapsed ? "sr-only" : "mb-0.5",
                  )}
                >
                  {section.label}
                </div>
                {section.items.map((item) => {
                  const matches =
                    pathname === item.href ||
                    pathname?.startsWith(item.href + "/");
                  // Only highlight the most specific (longest) matching href so
                  // e.g. /operations/leases highlights Leases, not Operations Overview
                  const isActive =
                    matches &&
                    !navigationItems.some(
                      (other) =>
                        other.href !== item.href &&
                        (pathname === other.href ||
                          pathname?.startsWith(other.href + "/")) &&
                        other.href.length > item.href.length,
                    );
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={cn(
                        "flex items-center rounded-lg py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        collapsed ? "justify-center px-2" : "gap-3 px-3",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                      aria-label={collapsed ? item.name : undefined}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className="h-5 w-5" />
                      <span
                        className={cn(
                          "truncate",
                          collapsed ? "hidden lg:inline-block lg:sr-only" : "",
                        )}
                      >
                        {item.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            ))}

            {/* Admin navigation - only visible to admins */}
            {isAdmin && (
              <>
                <div
                  className={cn("my-2 border-t", collapsed ? "mx-1" : "mx-2")}
                />
                {adminNavigation.map((item) => {
                  const isActive =
                    pathname === item.href ||
                    pathname?.startsWith(item.href + "/");
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={cn(
                        "flex items-center rounded-lg py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        collapsed ? "justify-center px-2" : "gap-3 px-3",
                        isActive
                          ? "bg-destructive text-destructive-foreground"
                          : "text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                      aria-label={collapsed ? item.name : undefined}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className="h-5 w-5" />
                      <span
                        className={cn(
                          "truncate",
                          collapsed ? "hidden lg:inline-block lg:sr-only" : "",
                        )}
                      >
                        {item.name}
                      </span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
}
